import { getAuth } from 'firebase/auth';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

/**
 * Authentication emails sent by the backend (Brevo) instead of by Firebase from the browser:
 * the Firebase emails sometimes never arrived and their failures were silent.
 * 'unavailable' means the backend could not send: the caller falls back to Firebase.
 * 'sessionExpired' means the backend refused the ID token (401): the user must log in again.
 */
export type AuthEmailOutcome =
  | 'sent' | 'alreadyVerified' | 'tooMany' | 'sessionExpired' | 'unavailable';

export interface AuthEmailResult {
  outcome: AuthEmailOutcome;
  /** For 'tooMany': seconds to wait before asking again (Retry-After, else the backend gap). */
  retryAfterSec?: number;
}

/** Minimum gap between two emails to the same address enforced by the backend (60 s). */
export const AUTH_EMAIL_MIN_GAP_SEC = 60;

export function parseRetryAfter(header: string | null): number {
  const seconds = header ? Number.parseInt(header, 10) : NaN;
  return Number.isFinite(seconds) && seconds > 0 ? Math.min(seconds, 3600) : AUTH_EMAIL_MIN_GAP_SEC;
}

async function postAuthEmail(
  path: string,
  body: Record<string, string>,
  token?: string,
): Promise<AuthEmailResult> {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const response = await fetch(`${API_BASE}/api/v1/email/${path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    if (response.status === 200) {
      const data = await response.json().catch(() => ({}));
      return { outcome: data?.status === 'already_verified' ? 'alreadyVerified' : 'sent' };
    }
    if (response.status === 429) {
      return {
        outcome: 'tooMany',
        retryAfterSec: parseRetryAfter(response.headers?.get('Retry-After') ?? null),
      };
    }
    if (response.status === 401 && token) {
      return { outcome: 'sessionExpired' };
    }
    return { outcome: 'unavailable' };
  } catch {
    return { outcome: 'unavailable' };
  }
}

export function requestPasswordResetEmail(
  email: string,
  locale: string,
): Promise<AuthEmailResult> {
  return postAuthEmail('password-reset', { email: email.trim(), locale });
}

export async function requestVerificationEmail(locale: string): Promise<AuthEmailResult> {
  const user = getAuth().currentUser;
  if (!user) {
    return { outcome: 'sessionExpired' };
  }
  let token: string;
  try {
    // A fresh token, so that an address verified a moment ago is seen as verified.
    token = await user.getIdToken(true);
  } catch {
    return { outcome: 'unavailable' };
  }
  return postAuthEmail('verification', { locale }, token);
}
